import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

export type RoomNightsReportRow = {
  roomTypeName?: string;
  roomNights?: number;
  bookings?: number;
  avgRate?: number;
  revenue?: number;
};

type InsightStyle = 'green' | 'success' | 'blue' | 'yellow' | 'red';

type RoomNightsReportData = {
  name?: string;
  reportType?: string;
  rows?: RoomNightsReportRow[];
  kpiMap?: Record<string, string>;
  infoText?: string;
  insightText?: string;
  insightStyle?: InsightStyle;
};

interface Props {
  data: RoomNightsReportData;
  name: string;
  handleExport?: () => void;
  showButton?: boolean;
}

const ROOM_NIGHT_KPI_CONFIG = [
  { label: 'Room Nights', accent: '#3B82F6' },
  { label: 'Room Revenue', accent: '#22C55E' },
  { label: 'Average Daily Rate (ADR)', accent: '#EAB308' },
] as const;

function formatCount(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
}

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  borderBottom: '1px solid #E5E7EB',
  py: 1,
  whiteSpace: 'nowrap',
};

const bodyCellSx = {
  fontSize: '13px',
  color: '#1F2937',
  borderBottom: '1px solid #F1F5F9',
  py: 1.1,
};

const RoomNightsKpiStrip = ({ kpiMap }: { kpiMap?: Record<string, string> }) => {
  const items = ROOM_NIGHT_KPI_CONFIG.map(config => ({
    ...config,
    value: kpiMap?.[config.label] ?? '—',
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1px',
        mb: '10px',
        flexShrink: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        pb: 0.25,
        '&::-webkit-scrollbar': { height: 2 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'action.disabled',
          borderRadius: 2,
        },
      }}>
      {items.map((item, index) => (
        <Box
          key={item.label}
          sx={{
            minWidth: '28%',
            flex: '1 0 auto',
            px: 1,
            py: 0.5,
            borderRight: index < items.length - 1 ? '1px solid #E5E7EB' : 'none',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                bgcolor: item.accent,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '15px',
                  lineHeight: 1.2,
                  fontVariantNumeric: 'tabular-nums',
                  color: '#111827',
                }}
                noWrap
                title={item.value}>
                {item.value}
              </Typography>
              <Typography
                sx={{
                  display: 'block',
                  fontWeight: 600,
                  fontSize: '9px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  lineHeight: 1.2,
                  color: '#9CA3AF',
                  mt: '2px',
                }}
                noWrap
                title={item.label}>
                {item.label}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RoomNightsReportTable: React.FC<Props> = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name || 'Room Nights Report';
  const rows = data?.rows || [];
  const hasKpis = useMemo(
    () => ROOM_NIGHT_KPI_CONFIG.some(config => data?.kpiMap?.[config.label] != null),
    [data?.kpiMap],
  );

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader title={displayName} insight={data?.infoText} handleExport={handleExport} showButton={showButton} />
      {hasKpis && <RoomNightsKpiStrip kpiMap={data?.kpiMap} />}
      {data?.insightText && <ReportInsightBanner text={data.insightText} style={data.insightStyle} />}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mt: 0.5 }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              color: '#94A3B8',
            }}>
            <Typography variant="body2">No room night data for the selected period.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Room Type</TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Room Nights
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Bookings
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    ADR
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Revenue
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={`${row.roomTypeName || 'row'}-${index}`}
                    sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row.roomTypeName || '—'}</TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(row.roomNights)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(row.bookings)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrencyWithSymbol(row.avgRate ?? 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrencyWithSymbol(row.revenue ?? 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
};

export default RoomNightsReportTable;
